import { filter, some, first, concat } from 'lodash';
import { useAppUser } from '@bechtel/client-common';
import { CrewMemberAssignment, AuthPermission, AuthScope, PersonaType, OwnershipType } from '@bechtel/types';
import { useWorkPackagePermissions } from '~/hooks/use-work-package-permissions';

export function useWorkOwnerEditPermissions(
  primaryOwners: CrewMemberAssignment[],
  secondaryOwners: CrewMemberAssignment[],
  otherOwners: CrewMemberAssignment[],
  ownerType?: OwnershipType
) {
  const userPermissions = useWorkPackagePermissions(concat(primaryOwners, secondaryOwners, otherOwners));
  const { user, hasPermissionAndScope } = useAppUser();

  const isUserPrimaryOwner = some(primaryOwners, (owner) => owner?.crewMember?.id === user?.id);
  const isUserSecondaryOwner = some(secondaryOwners, (owner) => owner?.crewMember?.id === user?.id);
  const isUserOtherOwner = some(otherOwners, (owner) => owner?.crewMember?.id === user?.id);
  const currentOwners =
    ownerType === OwnershipType.PRIMARY
      ? primaryOwners
      : ownerType === OwnershipType.SECONDARY
      ? secondaryOwners
      : otherOwners;
  const primarySuperintendentId = first(filter(primaryOwners, (owner) => owner?.persona === PersonaType.SUPERINTENDENT))
    ?.crewMember?.id;
  const primaryGeneralForemanId = first(
    filter(primaryOwners, (owner) => owner?.persona === PersonaType.GENERAL_FOREMAN)
  )?.crewMember?.id;
  const superintendentId = first(filter(currentOwners, (owner) => owner?.persona === PersonaType.SUPERINTENDENT))
    ?.crewMember?.id;
  const generalForemanId = first(filter(currentOwners, (owner) => owner?.persona === PersonaType.GENERAL_FOREMAN))
    ?.crewMember?.id;
  const foremanId = first(filter(currentOwners, (owner) => owner?.persona === PersonaType.FOREMAN))?.crewMember?.id;

  const canEditSuperintendent =
    hasPermissionAndScope(AuthPermission.EDIT_SUPERINTENDENT_WORK_PACKAGE, AuthScope.ALL_WORK_PACKAGES) || //User with All workpackage permissions (OR)
    (userPermissions.canEditSuperintendent && //Should have this edit superintendent permission for his WP (AND)
      (isUserPrimaryOwner || //IsPrimarySuperintendent
        (isUserSecondaryOwner && ownerType === OwnershipType.SECONDARY) || //Secondary Superintendent can unassign himself/assign another to his spot
        (isUserOtherOwner && ownerType === OwnershipType.OTHER))); //Other Superintedent can unassign himself/assign another to his spot

  const canEditGeneralForeman =
    hasPermissionAndScope(AuthPermission.EDIT_GENERAL_FOREMAN_WORK_PACKAGE, AuthScope.ALL_WORK_PACKAGES) || //User with All workpackage permissions (OR)
    (userPermissions.canEditGeneralForeman && //Should have this edit general foreman permission for his WP (AND)
      (canEditSuperintendent || // Primary Superintendent can edit
        (isUserPrimaryOwner && primarySuperintendentId === superintendentId) || //Primary GF can edit secondary/other GF if Superintendent is same
        user?.id === generalForemanId)); //User can edit himself

  const canEditForeman =
    hasPermissionAndScope(AuthPermission.EDIT_FOREMAN_WORK_PACKAGE, AuthScope.ALL_WORK_PACKAGES) ||
    (userPermissions.canEditForeman &&
      (canEditSuperintendent || // Primary Superintendent can edit
        canEditGeneralForeman || //Primary GF can edit secondary/other FM if Superintendent is same
        (isUserPrimaryOwner && primaryGeneralForemanId === generalForemanId) || //Primary FM can edit secondary/other FM if GF is same
        user?.id === foremanId)); //User can edit himself
  return {
    canEditSuperintendent: canEditSuperintendent,
    canEditGeneralForeman: canEditGeneralForeman,
    canEditForeman: canEditForeman,
    canEditFieldEngineer: userPermissions.canEditFieldEngineer,
  };
}
